import { ILCHttpResponse } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import { IAiReportExistsMap, IAiReportGetResponse, IAiReportPublic } from '../interfaces/ai-report.interface'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export interface IGenerateCaseAiReportRequest {
  caseId: number
  locale?: string
  regenerate?: boolean
}

export interface IGenerateAssessmentAiReportRequest {
  evaluationId: string
  identity: string
  locale?: string
  regenerate?: boolean
}

// @lat: [[state#AI reports]]
export const aiReportsApi = createApi({
  reducerPath: 'aiReportsApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/ai-reports`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  tagTypes: ['AiReport', 'AiReportExists'],
  endpoints: (builder) => ({
    getCaseAiReport: builder.query<IAiReportGetResponse, number>({
      query: (caseId) => ({
        url: `/case/${caseId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, caseId) => [{ type: 'AiReport', id: `case-${caseId}` }],
      transformResponse: (response: ILCHttpResponse<IAiReportGetResponse>) => response.data as IAiReportGetResponse,
    }),
    getAssessmentAiReport: builder.query<IAiReportGetResponse, { evaluationId: string; identity: string }>({
      query: ({ evaluationId, identity }) => ({
        url: `/assessment/${evaluationId}`,
        method: 'GET',
        params: { identity },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'AiReport', id: `assessment-${arg.evaluationId}` }],
      transformResponse: (response: ILCHttpResponse<IAiReportGetResponse>) => response.data as IAiReportGetResponse,
    }),
    getAiReportsExists: builder.query<IAiReportExistsMap, string[]>({
      query: (evaluationIds) => ({
        url: '/assessments/exists',
        method: 'GET',
        params: { evaluationIds: evaluationIds.join(',') },
      }),
      providesTags: ['AiReportExists'],
      transformResponse: (response: ILCHttpResponse<IAiReportExistsMap>) => response.data || {},
    }),
    generateCaseAiReport: builder.mutation<IAiReportPublic, IGenerateCaseAiReportRequest>({
      query: ({ caseId, locale, regenerate }) => ({
        url: `/case/${caseId}`,
        method: 'POST',
        params: locale ? { locale } : {},
        body: { regenerate },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'AiReport', id: `case-${arg.caseId}` }, 'AiReportExists'],
      transformResponse: (response: ILCHttpResponse<IAiReportPublic>) => response.data as IAiReportPublic,
    }),
    generateAssessmentAiReport: builder.mutation<IAiReportPublic, IGenerateAssessmentAiReportRequest>({
      query: ({ evaluationId, identity, locale, regenerate }) => ({
        url: `/assessment/${evaluationId}`,
        method: 'POST',
        params: locale ? { locale } : {},
        body: { identity, regenerate },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'AiReport', id: `assessment-${arg.evaluationId}` }, 'AiReportExists'],
      transformResponse: (response: ILCHttpResponse<IAiReportPublic>) => response.data as IAiReportPublic,
    }),
  }),
})

export const {
  useGetCaseAiReportQuery,
  useGetAssessmentAiReportQuery,
  useGetAiReportsExistsQuery,
  useGenerateCaseAiReportMutation,
  useGenerateAssessmentAiReportMutation,
} = aiReportsApi
